
import * as React from "react";

import { cn } from "@/lib/utils";
import { ScrollIndicator } from "@/components/scroll-indicator";

function Table({ className, disableScroll, ...props }: React.ComponentProps<"table"> & { disableScroll?: boolean }) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  if (disableScroll) {
    return <table data-slot="table" className={cn("w-full caption-bottom text-sm", className)} {...props} />;
  }

  return (
    <div className="relative">
      <ScrollIndicator containerRef={containerRef as React.RefObject<HTMLDivElement>} />
      <div 
        ref={containerRef}
        data-slot="table-container" 
        className="relative w-full overflow-x-auto scrollbar-visible"
        style={{
          // Ensure scrollbar is always visible and styled
          scrollbarWidth: 'thin',
          scrollbarColor: 'hsl(var(--border)) transparent',
        }}
      >
        <table data-slot="table" className={cn("w-full caption-bottom text-sm", className)} {...props} />
      </div>
    </div>
  );
}

function TableHeader({ className, divided, ...props }: React.ComponentProps<"thead"> & { divided?: boolean }) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "[&_tr]:border-b",
        divided && "border-t border-border [&_th]:border-r [&_th]:border-border [&_th:last-child]:border-r-0",
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn("hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors", className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:text-center [&:has([role=checkbox])]:align-middle",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption data-slot="table-caption" className={cn("text-muted-foreground mt-4 text-sm", className)} {...props} />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
